import React from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '../components/SEOHead';
import { BreadcrumbSchema } from '../components/JsonLdSchema';

const sections = [
  {
    id: 'section-1', num: '1', title: 'Subscription Fees and Activation',
    points: [
      { label: 'Payment Requirement', text: 'Users must pay the designated fee ("Subscription Fee") associated with their chosen plan to utilize our services. Applicable taxes will be added to the final price.' },
      { label: 'Activation', text: 'Your DPOS account and associated features will only become active once the payment has been successfully processed.' },
      { label: 'General Non-Refundability', text: 'Unless explicitly stated otherwise in this policy, all Subscription Fees are strictly non-refundable. Furthermore, any food or third-party services ordered through our platform are not eligible for refunds.' },
    ]
  },
  {
    id: 'section-2', num: '2', title: 'Cancellation Process & Refund Eligibility',
    intro: 'Users have the flexibility to cancel their active DPOS subscription at any moment by navigating to the account settings within the platform. Refunds are handled strictly under the following conditions:',
    points: [
      { label: 'The 15-Day Window (All Plans)', text: 'If you choose to cancel your subscription within the first fifteen (15) business days of signing up, you are eligible for a full refund via a credit note. However, the Company reserves the right to issue a partial credit note by deducting an amount equivalent to the value of the platform services you have already utilized during those 15 days.' },
      { label: 'Annual Subscriptions (After 15 Days)', text: 'If an annual subscriber cancels after the initial 15-business-day period has passed, we will issue a credit note for a prorated refund. This amount will be calculated strictly based on the unused portion of your subscription.' },
      { label: 'Processing Time', text: "Approved refunds will be processed and credited back to the customer's original bank account within thirty (30) days." },
    ]
  },
  {
    id: 'section-3', num: '3', title: 'Policy Modifications',
    body: 'DGPOS LLP reserves the right to amend, update, or modify this Cancellation & Refund Policy, our Terms of Service, or any features of the DPOS Platform at our sole discretion, without prior notice. If any provision within these updates is deemed legally invalid, it will not affect the enforceability of the remaining terms. By continuing to use your DPOS subscription after any changes are published, you are acknowledging and accepting the new terms. If you disagree with any updates, your sole recourse is to cancel your subscription.'
  },
  {
    id: 'section-4', num: '4', title: 'Termination by DGPOS LLP',
    intro: 'We maintain the right to revoke access or terminate your DPOS subscription at any time, without prior warning.',
    points: [
      { label: 'Standard Termination', text: 'If we close your account under normal circumstances, you will be issued a credit note for a prorated refund covering the remainder of your active billing cycle (monthly or annual).' },
      { label: 'Termination for Cause', text: 'We will not issue refunds of any kind if your account is terminated due to fraudulent activity, misuse of the platform, violation of our Terms of Service, illegal conduct, or behavior that harms DGPOS LLP or other users. Our failure to immediately enforce any of these rules does not waive our right to take action in the future.' },
    ]
  },
];

const toc = [
  { id: 'section-1', label: '1. Subscription Fees and Activation' },
  { id: 'section-2', label: '2. Cancellation Process & Refund Eligibility' },
  { id: 'section-3', label: '3. Policy Modifications' },
  { id: 'section-4', label: '4. Termination by DGPOS LLP' },
];

export default function CancellationPolicy() {
  return (
    <main style={{ paddingTop: 64 }}>
      <SEOHead
        title="Cancellation & Refund Policy – DPOS"
        description="Read the DPOS subscription cancellation and refund policy, including eligibility conditions and the cancellation process."
        canonical="/cancellation-policy"
      />
      <BreadcrumbSchema items={[
        { name: 'Home', path: '/' },
        { name: 'Cancellation & Refund Policy' },
      ]} />
      <section style={{ padding: '72px 24px 80px' }}>
        <div style={{ maxWidth: 1280, margin: '0 auto', display: 'grid', gridTemplateColumns: '280px 1fr', gap: 64, alignItems: 'flex-start' }} className="legal-grid">

          {/* Sidebar */}
          <div style={{ position: 'sticky', top: 88 }}>
            <h1 style={{ fontSize: 28, fontWeight: 800, color: '#111827', marginBottom: 4 }}>Cancellation & Refund Policy</h1>
            <p style={{ fontSize: 13, color: '#9ca3af', marginBottom: 28 }}>Last Updated: 26-06-2026</p>
            <p style={{ fontSize: 12, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 12 }}>Table of Contents</p>
            <nav style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {toc.map(t => (
                <a key={t.id} href={`#${t.id}`} style={{ fontSize: 13, color: '#6b7280', padding: '6px 12px', borderRadius: 6, transition: 'all 0.15s' }}
                  onMouseEnter={e => { e.target.style.background = '#fef2f2'; e.target.style.color = '#CC0000'; }}
                  onMouseLeave={e => { e.target.style.background = 'transparent'; e.target.style.color = '#6b7280'; }}
                >{t.label}</a>
              ))}
            </nav>
            <div style={{ marginTop: 32, background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 12, padding: 20 }}>
              <p style={{ fontSize: 13, fontWeight: 600, color: '#111827', marginBottom: 6 }}>Need Help?</p>
              <p style={{ fontSize: 12, color: '#6b7280', marginBottom: 12 }}>If you have any questions about this policy, please contact us.</p>
              <Link to="/contact" style={{ fontSize: 13, fontWeight: 600, color: '#CC0000' }}>Contact Support →</Link>
            </div>
          </div>

          {/* Content */}
          <div>
            <p style={{ fontSize: 15, color: '#6b7280', lineHeight: 1.8, marginBottom: 40, paddingBottom: 40, borderBottom: '1px solid #e5e7eb' }}>
              Welcome to DPOS, a cloud-based Point of Sale (POS) ecosystem designed specifically for the restaurant industry. DPOS is developed, owned, and operated by DGPOS LLP (referred to herein as "Company", "We", "Us", or "Our"). By purchasing a subscription to the DPOS software and its associated websites (collectively, the "DPOS Platforms"), you (the "User") agree to be bound by our Terms of Service and this Cancellation & Refund Policy.
            </p>

            {sections.map(s => (
              <div key={s.id} id={s.id} style={{ marginBottom: 56, paddingBottom: 56, borderBottom: '1px solid #f3f4f6' }}>
                <h2 style={{ fontSize: 22, fontWeight: 800, color: '#111827', marginBottom: 16 }}>
                  {s.num}. {s.title}
                </h2>
                {s.intro && <p style={{ fontSize: 15, color: '#374151', lineHeight: 1.8, marginBottom: 20 }}>{s.intro}</p>}
                {s.body && <p style={{ fontSize: 15, color: '#374151', lineHeight: 1.8 }}>{s.body}</p>}
                {s.points && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    {s.points.map(pt => (
                      <div key={pt.label} style={{ display: 'flex', gap: 12, alignItems: 'flex-start', background: '#f9fafb', borderRadius: 10, padding: '14px 18px', border: '1px solid #e5e7eb' }}>
                        <span style={{ color: '#CC0000', fontWeight: 700, fontSize: 16, flexShrink: 0, marginTop: 2 }}>•</span>
                        <p style={{ fontSize: 14, color: '#374151', lineHeight: 1.8 }}>
                          <strong style={{ color: '#111827' }}>{pt.label}:</strong> {pt.text}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>
      <style>{`@media(max-width:900px){.legal-grid{grid-template-columns:1fr !important;}}`}</style>
    </main>
  );
}
